import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Stack, Grid, Button } from '@mui/material';
import { useSnackbar } from 'notistack';
import Brightness1Icon from '@mui/icons-material/Brightness1';
import { getLibrarySeatLayout, getSeatAvailability, getLibraryBookingBySeatId } from 'services/seatLayout';
import { useParams } from "react-router-dom";
import { assignSeatsToBooking } from 'services/bookings';
import UserPopper from './UserPopper';
// import { useNavigate} from "react-router-dom";

const SeatLayout = ({
  availabilityFilters = null, 
  availableSeats, 
  libData=null,
  onSeatAssigned, 
  onSeatSelectedListener, 
  hideSaveButton = false,
  selectedSeatLegend="Booked",
  allowBooking=false, 
  showBookingDetails=false,
  bookSeatsHandler,
  useBackBtn=true,
}) => {

  const { enqueueSnackbar } = useSnackbar();
  const [seatLayoutData, setSeatLayoutData] = useState([]);
  let { libraryId: libraryIdParam } = useParams();
  const [selectedSeats, setSelectedSeats] = useState(null);
  const [openPopper, setOpenPopper] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [popperContent, setPopperContent] = useState();

  const [libraryId] = useState(libraryIdParam || (libData?.libraryId) || null);
  // let navigate = useNavigate();

  const buildSeatLayout = (seatNumbers = []) => {
    const noOfColumGroup = 2;
    const seatsPerColumn = 2;
    const noOfColums = noOfColumGroup * seatsPerColumn;
    const noOfRows = Math.ceil(seatNumbers.length / noOfColums);
    const data = [];

    for(let row=0; row < noOfRows; row++) {
      const arrayIndex = row*noOfColums;
      const rowData = seatNumbers.slice(arrayIndex,arrayIndex+noOfColums);
      // const columnsPerRow = Math.ceil(rowData.length/seatsPerColumn);
      const finalRowData = [];
      // seat column 
      for(let c = 0; c < noOfColumGroup; c++) {
        const columnIndex = c*seatsPerColumn;
        const columnSeatData = rowData.slice(columnIndex,columnIndex+seatsPerColumn);
          finalRowData.push([...columnSeatData]);
        }
      data.push([...finalRowData]);
    }
    return data;
  }

  useEffect(() => {
    if(Array.isArray(selectedSeats) && typeof onSeatSelectedListener === 'function'){
      onSeatSelectedListener(selectedSeats);
    }
  }, [selectedSeats, onSeatSelectedListener]);
  

  const getSeatLayout = () => {
      getLibrarySeatLayout({libraryId}).then(({response, error}) => {
        if(response){
          const x = buildSeatLayout(response);
          setSeatLayoutData([...x]);
        } else {
          enqueueSnackbar((response?.message || error), {variant: 'error'});
        }
      });
  };

  useEffect(() => {
    if(availabilityFilters){
      getSeatLayoutWithAvailability(availabilityFilters);
    }else if(libraryId && !availableSeats) {
      getSeatLayout();
    }else if(availableSeats){
      const x = buildSeatLayout(availableSeats);
      setSeatLayoutData([...x]);
    }
  }, [availabilityFilters, availableSeats]);
  

  function getSeatLayoutWithAvailability(filters) {
    getSeatAvailability({libraryId, ...filters}).then(({response, error}) => {
      if(response){
        const x = buildSeatLayout(response);
        setSeatLayoutData([...x]);
      } else {
        enqueueSnackbar((response?.message || error), {variant: 'error'});
      }
    });
  }

  const showPopper = (event, seatId) => {
    if(showBookingDetails){
      if(anchorEl && anchorEl !== event.currentTarget){
        setAnchorEl(event.currentTarget);  
        getLibraryBookingBySeatId({libraryId, seatId, fromTime: availabilityFilters?.fromTime, date: availabilityFilters?.fromDate}).then(({response, error}) => {
          if(response){
            const {user} = response;
            setPopperContent({...user});
            setOpenPopper(true);
          } else {
            setOpenPopper(false);
            enqueueSnackbar((response?.message || error), {variant: 'error'});
          }
        })
      }else{
        setOpenPopper((previousOpen) => !previousOpen);
      }
    }
  };

  function onSeatSelected(e, seat) {
    e.preventDefault();
    if(availableSeats || availabilityFilters){
      if(seat.booked){
        showPopper(e, seat?.id);
        return false;
        // enqueueSnackbar("Seat is already booked", {variant: 'warning'});
      }else {
        const seatId = seat.id;
        if(Array.isArray(selectedSeats) && selectedSeats.includes(seatId)){
          setSelectedSeats([...selectedSeats.filter((id) => id !== seatId)]);
        }else{
          setSelectedSeats([...(selectedSeats || []), seatId]);
        }
      }
    }
    return false;
  }

  function buildSeatSourceUrl(seat) {
    let url = '/assets/images/desk-chair-black.svg';
    if(seat.booked) {
      url = '/assets/images/desk-chair.svg';
    }
    if(Array.isArray(selectedSeats) && selectedSeats.includes(seat.id)) {
      url = '/assets/images/desk-chair-yellow.svg';
    }
    return url;
  }

  function assignSeats() {
    const {bookingId, libraryId: libId} = (libData || {});

    if(libData.seatCount !== selectedSeats.count){
      enqueueSnackbar(`This booking is for ${libData.seatCount} seat/s, please select ${libData.seatCount} seat/s to proceed.`, {variant: 'error'});
      return;
    }

    if(Array.isArray(selectedSeats) && selectedSeats?.length > 0  && bookingId && libId){
      assignSeatsToBooking({libraryId: libId, bookingId, seatIds: selectedSeats}).then(({response, error}) => {
        if(response){
          enqueueSnackbar((response?.message || "Seat assigned successfully"), {variant: 'success'});
          onSeatAssigned();
        } else {
          enqueueSnackbar((response?.message || error), {variant: 'error'});
        }
      });
    }
  }

  const onClickAway = useCallback((d)=>{
    if(anchorEl && anchorEl !== d?.target){
      setOpenPopper(false)
    }
  }, [anchorEl])
  

  return (
    <Grid container columnSpacing={4} rowSpacing={2} >
      <Grid container item xs={12} sm={12} lg={12} md={12} rowSpacing={2} columnSpacing={0} direction="row"  alignItems="center" justifyContent="flex-start">
       {availableSeats || availabilityFilters ? (
          <Grid item xs={12} sx={{mb:1}}>
            <Stack spacing={2} direction="row">
              <Brightness1Icon sx={{color: "#000"}} /> Available
              <Brightness1Icon sx={{color: "#AAB1BA"}} /> {selectedSeatLegend}
              {availableSeats ? <><Brightness1Icon color="primary" /> Selected</> : null}
            </Stack>
          </Grid>
       ) : null}
      <UserPopper user={popperContent} openPopper={openPopper} anchorEl={anchorEl} onClickAway={onClickAway} />
        {seatLayoutData.map((row, i) => (
          <Grid key={i} container item xs={12} sm={12} lg={12} md={12} sx={{mb: (i+1)%3 === 0 ? 4 : 0}} columnSpacing={12} direction="row"    alignItems="center" justifyContent="center">
              {row.map((column, j) => (
                <Grid key={j} container item xs={6} sm={5} xl={availableSeats ? 3 : 3} lg={3} md={3} columnSpacing={3}  alignItems="center" justifyContent="center">
                  {column.map((seat, index) => (
                    <Grid item xs={6} sm={6} xl={6} lg={6} md={6} sx={{textAlign: 'center'}} key={index}>
                      <img
                        style={{width: '30px', height: '30px', padding: '2px', cursor: availableSeats ?  'pointer' : 'initial' }}
                        src={buildSeatSourceUrl(seat)}
                        alt={`Seat ${seat.name}`}
                        onClick={(e) => onSeatSelected(e, seat)}
                      />
                      <p style={{margin: '0 0 8px 0'}}>
                        {seat.name}
                      </p>
                    </Grid>
                  ))}
                </Grid>
              ))}
            </Grid>
        ))}
      </Grid>
      <Grid item xs={12} sx={{mt:1, textAlign: 'center'}} alignItems="center">
          {/* {useBackBtn ? <Button variant="contained" onClick={() => navigate(-1)}>back</Button> : null} */}
          {allowBooking ===false && hideSaveButton === false && Array.isArray(selectedSeats) && selectedSeats?.length > 0 ? (
            <Button variant="contained" onClick={assignSeats}>Save</Button>
          ) : null}
          {allowBooking ===true && Array.isArray(selectedSeats) && selectedSeats?.length > 0 ? (
            <Button variant="contained" onClick={() => bookSeatsHandler(selectedSeats)}>Book Now</Button>
          ) : null}
        </Grid>
    </Grid>
  );
}
 
export default React.memo(SeatLayout);
